import type { TypedSupabaseClient } from '../client';
import { orContainsAnyColumn } from './postgrest-search';
import type {
  ChatMessage,
  ConnectWhatsAppInput,
  ConversationFilters,
  ConversationNote,
  ConversationSummary,
  DetectOrderInput,
  DetectOrderResult,
  IMessagingRepository,
  MessagingOrderDraft,
  MessagingPageChannel,
  OrderDraftFilters,
  QuickReply,
  QuickReplyInput,
  SendMessageResult,
  SendTemplateInput,
  WhatsAppSignupConfig,
  WhatsAppSignupInput,
  WhatsAppTemplate,
  UpdateOrderDraftInput,
  WhatsAppChannel,
} from '../../../interfaces/src';
import type {
  MessageAttachment,
  MessageDirection,
  MessageStatus,
  MessageType,
  MessagingPlatform,
  OrderDraftCandidate,
  OrderDraftItem,
  OrderDraftStatus,
} from '@erp/domain/messaging';
import { newAiOperationId } from './ai-operation-id';

// The generated Supabase types are hand-maintained & stale, so reach the new
// tables / RPCs through narrow casts (same idiom as social-repository).
const anyFrom = (client: TypedSupabaseClient) => (table: string) =>
  (client as unknown as { from: (t: string) => any }).from(table);
const anyRpc =
  (client: TypedSupabaseClient) =>
  (fn: string, args: Record<string, unknown>) =>
    (
      client as unknown as {
        rpc: (
          f: string,
          a: Record<string, unknown>,
        ) => Promise<{ data: any; error: any }>;
      }
    ).rpc(fn, args);

/**
 * functions.invoke errors hide the response payload inside error.context —
 * surface the { error, detail } body so the UI can map it to a message.
 */
async function invokeError(error: unknown, fallback: string): Promise<Error> {
  const ctx = (error as { context?: Response })?.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = (await ctx.json()) as { error?: string; detail?: string };
      if (body?.error) {
        const err = new Error(
          body.detail ? `${body.error}: ${body.detail}` : body.error,
        );
        (err as Error & { code?: string }).code = body.error;
        return err;
      }
    } catch {
      /* fall through */
    }
  }
  return error instanceof Error ? error : new Error(fallback);
}

export class SupabaseMessagingRepository implements IMessagingRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  // ---- Channels ----

  async listPageChannels(): Promise<MessagingPageChannel[]> {
    // SECURITY DEFINER RPC: non-secret columns only (never the page token).
    const { data, error } = await anyRpc(this.supabase)(
      'list_social_accounts',
      {},
    );
    if (error) throw error;
    return (data ?? []).map(
      (row: Record<string, unknown>): MessagingPageChannel => ({
        accountId: row['id'] as string,
        pageId: (row['fb_page_id'] as string) ?? '',
        pageName: (row['fb_page_name'] as string) ?? '',
        igUserId: (row['ig_user_id'] as string) ?? null,
        igUsername: (row['ig_username'] as string) ?? null,
        avatarUrl: (row['avatar_url'] as string) ?? null,
        isActive: Boolean(row['is_active']),
        messagingEnabled: Boolean(row['messaging_enabled']),
      }),
    );
  }

  async listWhatsAppChannels(): Promise<WhatsAppChannel[]> {
    const { data, error } = await anyRpc(this.supabase)(
      'list_whatsapp_accounts',
      {},
    );
    if (error) throw error;
    return (data ?? []).map(mapWhatsApp);
  }

  async startPageConnect(returnPath?: string): Promise<{ url: string }> {
    const { data, error } = await this.supabase.functions.invoke(
      'meta-oauth-start',
      {
        body: { returnPath: returnPath ?? '/messaging', scopeSet: 'messaging' },
      },
    );
    if (error) throw await invokeError(error, 'connect_start_failed');
    return { url: String((data as { url?: string })?.url ?? '') };
  }

  async enablePageMessaging(accountId: string): Promise<void> {
    const { error } = await this.supabase.functions.invoke(
      'messaging-connect',
      {
        body: { action: 'enable_page', accountId },
      },
    );
    if (error) throw await invokeError(error, 'enable_failed');
  }

  async disablePageMessaging(accountId: string): Promise<void> {
    const { error } = await this.supabase.functions.invoke(
      'messaging-connect',
      {
        body: { action: 'disable_page', accountId },
      },
    );
    if (error) throw await invokeError(error, 'disable_failed');
  }

  async connectWhatsApp(input: ConnectWhatsAppInput): Promise<WhatsAppChannel> {
    const { data, error } = await this.supabase.functions.invoke(
      'messaging-connect',
      {
        body: {
          action: 'connect_whatsapp',
          phoneNumberId: input.phoneNumberId,
          wabaId: input.wabaId ?? null,
          accessToken: input.accessToken,
        },
      },
    );
    if (error) throw await invokeError(error, 'whatsapp_connect_failed');
    const row = (data as { account?: Record<string, unknown> })?.account;
    if (!row) throw new Error('whatsapp_connect_failed');
    return mapWhatsApp(row);
  }

  async disconnectWhatsApp(id: string): Promise<void> {
    const { error } = await this.supabase.functions.invoke(
      'messaging-connect',
      {
        body: { action: 'disconnect_whatsapp', id },
      },
    );
    if (error) throw await invokeError(error, 'whatsapp_disconnect_failed');
  }

  async getWhatsAppSignupConfig(): Promise<WhatsAppSignupConfig> {
    const { data, error } = await this.supabase.functions.invoke(
      'messaging-whatsapp-signup',
      {
        body: { action: 'config' },
      },
    );
    if (error) throw await invokeError(error, 'signup_config_failed');
    const res = data as WhatsAppSignupConfig;
    return res?.configured ? res : { configured: false };
  }

  async completeWhatsAppSignup(
    input: WhatsAppSignupInput,
  ): Promise<WhatsAppChannel> {
    const { data, error } = await this.supabase.functions.invoke(
      'messaging-whatsapp-signup',
      {
        body: { action: 'exchange', ...input },
      },
    );
    if (error) throw await invokeError(error, 'signup_failed');
    const row = (data as { account?: Record<string, unknown> })?.account ?? {};
    return mapWhatsApp(row);
  }

  // ---- Conversations ----

  async listConversations(
    filters?: ConversationFilters,
  ): Promise<ConversationSummary[]> {
    let query = anyFrom(this.supabase)('messaging_conversations')
      .select('*')
      .order('last_message_at', { ascending: false, nullsFirst: false })
      .eq('is_archived', filters?.archived ?? false)
      .limit(filters?.limit ?? 100);
    if (filters?.platform) query = query.eq('platform', filters.platform);
    if (filters?.clientId) query = query.eq('client_id', filters.clientId);
    if (filters?.unreadOnly) query = query.gt('unread_count', 0);
    if (filters?.assignedTo)
      query = query.eq('assigned_to', filters.assignedTo);
    // `contains` = the row's tags are a superset of the requested ones (AND).
    if (filters?.tags?.length) query = query.contains('tags', filters.tags);
    const search = filters?.search?.trim();
    if (search) {
      query = query.or(
        orContainsAnyColumn(
          [
            'participant_name',
            'participant_username',
            'participant_phone',
            'last_message_preview',
          ],
          search,
        ),
      );
    }
    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []).map(mapConversation);
  }

  async getConversation(id: string): Promise<ConversationSummary | null> {
    const { data, error } = await anyFrom(this.supabase)(
      'messaging_conversations',
    )
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data ? mapConversation(data) : null;
  }

  async countUnread(): Promise<number> {
    const { count, error } = await anyFrom(this.supabase)(
      'messaging_conversations',
    )
      .select('id', { count: 'exact', head: true })
      .gt('unread_count', 0)
      .eq('is_archived', false);
    if (error) throw error;
    return count ?? 0;
  }

  async markRead(conversationId: string): Promise<void> {
    const { error } = await anyFrom(this.supabase)('messaging_conversations')
      .update({ unread_count: 0 })
      .eq('id', conversationId);
    if (error) throw error;
  }

  async setArchived(conversationId: string, archived: boolean): Promise<void> {
    const { error } = await anyFrom(this.supabase)('messaging_conversations')
      .update({ is_archived: archived })
      .eq('id', conversationId);
    if (error) throw error;
  }

  async linkClient(
    conversationId: string,
    clientId: string | null,
  ): Promise<void> {
    const { error } = await anyFrom(this.supabase)('messaging_conversations')
      .update({ client_id: clientId })
      .eq('id', conversationId);
    if (error) throw error;
  }

  async assign(conversationId: string, userId: string | null): Promise<void> {
    const { error } = await anyFrom(this.supabase)('messaging_conversations')
      .update({ assigned_to: userId })
      .eq('id', conversationId);
    if (error) throw error;
  }

  async setTags(conversationId: string, tags: string[]): Promise<void> {
    const { error } = await anyFrom(this.supabase)('messaging_conversations')
      .update({ tags })
      .eq('id', conversationId);
    if (error) throw error;
  }

  // ---- Internal notes ----

  async listNotes(conversationId: string): Promise<ConversationNote[]> {
    // RPC rather than a plain select: the author's display name lives in
    // auth.users, which authenticated clients cannot read directly.
    const { data, error } = await anyRpc(this.supabase)(
      'list_messaging_notes',
      {
        p_conversation_id: conversationId,
      },
    );
    if (error) throw error;
    return ((data ?? []) as Array<Record<string, unknown>>).map(mapNote);
  }

  async addNote(
    conversationId: string,
    body: string,
  ): Promise<ConversationNote> {
    const { data: auth } = await this.supabase.auth.getUser();
    const { data, error } = await anyFrom(this.supabase)('messaging_notes')
      .insert({
        conversation_id: conversationId,
        body,
        author_id: auth?.user?.id ?? null,
      })
      .select('*')
      .single();
    if (error) throw error;
    // The insert cannot join auth.users; the author's email arrives on the
    // next listNotes(). Returning the row keeps the optimistic update honest.
    return mapNote(data as Record<string, unknown>);
  }

  async deleteNote(noteId: string): Promise<void> {
    const { error } = await anyFrom(this.supabase)('messaging_notes')
      .delete()
      .eq('id', noteId);
    if (error) throw error;
  }

  // ---- Saved replies ----

  async listQuickReplies(): Promise<QuickReply[]> {
    const { data, error } = await anyFrom(this.supabase)(
      'messaging_quick_replies',
    )
      .select('*')
      .order('shortcut', { ascending: true });
    if (error) throw error;
    return ((data ?? []) as Array<Record<string, unknown>>).map(mapQuickReply);
  }

  async createQuickReply(input: QuickReplyInput): Promise<QuickReply> {
    const { data: auth } = await this.supabase.auth.getUser();
    const { data, error } = await anyFrom(this.supabase)(
      'messaging_quick_replies',
    )
      .insert({
        shortcut: input.shortcut,
        title: input.title,
        body: input.body,
        created_by: auth?.user?.id ?? null,
      })
      .select('*')
      .single();
    if (error) throw quickReplyError(error);
    return mapQuickReply(data as Record<string, unknown>);
  }

  async updateQuickReply(
    id: string,
    input: Partial<QuickReplyInput>,
  ): Promise<QuickReply> {
    const patch: Record<string, unknown> = {};
    if (input.shortcut !== undefined) patch['shortcut'] = input.shortcut;
    if (input.title !== undefined) patch['title'] = input.title;
    if (input.body !== undefined) patch['body'] = input.body;
    const { data, error } = await anyFrom(this.supabase)(
      'messaging_quick_replies',
    )
      .update(patch)
      .eq('id', id)
      .select('*')
      .single();
    if (error) throw quickReplyError(error);
    return mapQuickReply(data as Record<string, unknown>);
  }

  async deleteQuickReply(id: string): Promise<void> {
    const { error } = await anyFrom(this.supabase)('messaging_quick_replies')
      .delete()
      .eq('id', id);
    if (error) throw error;
  }

  // ---- Messages ----

  async listMessages(
    conversationId: string,
    limit = 200,
  ): Promise<ChatMessage[]> {
    // Take the most recent N, then hand them back oldest→newest for rendering.
    const { data, error } = await anyFrom(this.supabase)('messaging_messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data ?? []).map(mapMessage).reverse();
  }

  async sendMessage(
    conversationId: string,
    text: string,
  ): Promise<SendMessageResult> {
    const { data, error } = await this.supabase.functions.invoke(
      'messaging-send',
      {
        body: { conversationId, text },
      },
    );
    if (error) throw await invokeError(error, 'send_failed');
    const row = (data as { message?: Record<string, unknown> })?.message;
    return { ok: true, message: row ? mapMessage(row) : undefined };
  }

  // ---- WhatsApp templates ----

  async listWhatsAppTemplates(
    conversationId: string,
  ): Promise<WhatsAppTemplate[]> {
    const { data, error } = await this.supabase.functions.invoke(
      'messaging-templates',
      {
        body: { conversationId },
      },
    );
    if (error) throw await invokeError(error, 'templates_failed');
    const rows =
      (data as { templates?: Array<Record<string, unknown>> })?.templates ?? [];
    return rows.map(mapWhatsAppTemplate);
  }

  async sendTemplate(
    conversationId: string,
    input: SendTemplateInput,
  ): Promise<SendMessageResult> {
    const { data, error } = await this.supabase.functions.invoke(
      'messaging-send',
      {
        body: {
          conversationId,
          // The rendered preview doubles as the thread's stored text, so the
          // history reads like what the customer received.
          text: input.text,
          template: {
            name: input.name,
            language: input.language,
            components: input.components,
          },
        },
      },
    );
    if (error) throw await invokeError(error, 'send_failed');
    const row = (data as { message?: Record<string, unknown> })?.message;
    return { ok: true, message: row ? mapMessage(row) : undefined };
  }

  // ---- Order detection ----

  async detectOrder(input: DetectOrderInput): Promise<DetectOrderResult> {
    const { data, error } = await this.supabase.functions.invoke(
      'messaging-detect-order',
      {
        body: {
          operationId: newAiOperationId(),
          conversationId: input.conversationId ?? undefined,
          imagePaths: input.imagePaths ?? undefined,
          target: input.target ?? undefined,
          proCustomerId: input.proCustomerId ?? undefined,
        },
      },
    );
    if (error) throw await invokeError(error, 'detect_failed');
    const payload = data as {
      noOrder?: boolean;
      draft?: Record<string, unknown>;
      confidence?: number;
      balanceCredits?: number;
      costCredits?: number;
    };
    return {
      noOrder: payload?.noOrder ?? false,
      draft: payload?.draft ? mapDraft(payload.draft) : undefined,
      confidence: payload?.confidence,
      balanceCredits: payload?.balanceCredits,
      costCredits: payload?.costCredits,
    };
  }

  async listOrderDrafts(
    filters?: OrderDraftFilters,
  ): Promise<MessagingOrderDraft[]> {
    let query = anyFrom(this.supabase)('messaging_order_drafts')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(filters?.limit ?? 50);
    if (filters?.conversationId)
      query = query.eq('conversation_id', filters.conversationId);
    if (filters?.source) query = query.eq('source', filters.source);
    if (filters?.target) query = query.eq('target', filters.target);
    if (filters?.status) query = query.eq('status', filters.status);
    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []).map(mapDraft);
  }

  async updateOrderDraft(
    id: string,
    input: UpdateOrderDraftInput,
  ): Promise<MessagingOrderDraft> {
    const row: Record<string, unknown> = {};
    if (input.status !== undefined) row['status'] = input.status;
    if (input.items !== undefined) row['items'] = input.items;
    if (input.notes !== undefined) row['notes'] = input.notes;
    if (input.salesDocumentId !== undefined)
      row['sales_document_id'] = input.salesDocumentId;
    if (input.target !== undefined) row['target'] = input.target;
    if (input.proCustomerId !== undefined)
      row['pro_customer_id'] = input.proCustomerId;
    if (input.proCommandeCode !== undefined)
      row['pro_commande_code'] = input.proCommandeCode;
    if (input.customer !== undefined) {
      row['customer_name'] = input.customer.name ?? null;
      row['customer_phone'] = input.customer.phone ?? null;
      row['customer_address'] = input.customer.address ?? null;
    }
    const { data, error } = await anyFrom(this.supabase)(
      'messaging_order_drafts',
    )
      .update(row)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapDraft(data);
  }
}

// ---- mappers ----

function mapWhatsApp(row: Record<string, unknown>): WhatsAppChannel {
  return {
    id: row['id'] as string,
    phoneNumberId: (row['phone_number_id'] as string) ?? '',
    wabaId: (row['waba_id'] as string) ?? null,
    displayPhoneNumber: (row['display_phone_number'] as string) ?? null,
    verifiedName: (row['verified_name'] as string) ?? null,
    isActive: Boolean(row['is_active'] ?? true),
    lastError: (row['last_error'] as string) ?? null,
    createdAt: (row['created_at'] as string) ?? '',
  };
}

function mapConversation(row: Record<string, unknown>): ConversationSummary {
  return {
    id: row['id'] as string,
    platform: (row['platform'] as MessagingPlatform) ?? 'messenger',
    channelExternalId: (row['channel_external_id'] as string) ?? '',
    participantExternalId: (row['participant_external_id'] as string) ?? '',
    participantName: (row['participant_name'] as string) ?? null,
    participantUsername: (row['participant_username'] as string) ?? null,
    participantAvatarUrl: (row['participant_avatar_url'] as string) ?? null,
    participantPhone: (row['participant_phone'] as string) ?? null,
    clientId: (row['client_id'] as string) ?? null,
    lastMessageAt: (row['last_message_at'] as string) ?? null,
    lastMessagePreview: (row['last_message_preview'] as string) ?? null,
    lastMessageType: (row['last_message_type'] as MessageType) ?? null,
    lastMessageDirection:
      (row['last_message_direction'] as MessageDirection) ?? null,
    lastInboundAt: (row['last_inbound_at'] as string) ?? null,
    unreadCount: Number(row['unread_count'] ?? 0),
    isArchived: Boolean(row['is_archived']),
    assignedTo: (row['assigned_to'] as string) ?? null,
    // Rows created before the triage migration have no column at all, so the
    // mapper — not the UI — is where the empty list is guaranteed.
    tags: Array.isArray(row['tags']) ? (row['tags'] as string[]) : [],
    createdAt: (row['created_at'] as string) ?? '',
  };
}

/**
 * Meta's payload passes through almost verbatim — the domain layer is what
 * decides which components it can fill, so narrowing here would only hide
 * formats it already knows how to reject.
 */
function mapWhatsAppTemplate(row: Record<string, unknown>): WhatsAppTemplate {
  const components = Array.isArray(row['components'])
    ? (row['components'] as Array<Record<string, unknown>>).map((c) => ({
        type: String(
          c['type'] ?? '',
        ).toUpperCase() as WhatsAppTemplate['components'][number]['type'],
        format: c['format']
          ? (String(c['format']).toUpperCase() as NonNullable<
              WhatsAppTemplate['components'][number]['format']
            >)
          : undefined,
        text: typeof c['text'] === 'string' ? c['text'] : undefined,
      }))
    : [];
  return {
    id: String(row['id'] ?? ''),
    name: String(row['name'] ?? ''),
    language: String(row['language'] ?? ''),
    status: String(
      row['status'] ?? '',
    ).toUpperCase() as WhatsAppTemplate['status'],
    category: row['category'] ? String(row['category']) : null,
    components,
  };
}

function mapQuickReply(row: Record<string, unknown>): QuickReply {
  return {
    id: row['id'] as string,
    shortcut: (row['shortcut'] as string) ?? '',
    title: (row['title'] as string) ?? '',
    body: (row['body'] as string) ?? '',
    createdAt: (row['created_at'] as string) ?? '',
  };
}

/**
 * The shortcut is unique per tenant, and colliding with an existing one is the
 * mistake a manager actually makes. Surface it as a code the form can map to
 * a field error instead of a raw Postgres message.
 */
function quickReplyError(error: unknown): Error {
  const code = (error as { code?: string })?.code;
  if (code === '23505') {
    const err = new Error('quick_reply_shortcut_taken');
    (err as Error & { code?: string }).code = 'quick_reply_shortcut_taken';
    return err;
  }
  if (code === '23514') {
    const err = new Error('quick_reply_invalid');
    (err as Error & { code?: string }).code = 'quick_reply_invalid';
    return err;
  }
  return error instanceof Error ? error : new Error('quick_reply_failed');
}

function mapNote(row: Record<string, unknown>): ConversationNote {
  return {
    id: row['id'] as string,
    conversationId: (row['conversation_id'] as string) ?? '',
    body: (row['body'] as string) ?? '',
    authorId: (row['author_id'] as string) ?? null,
    authorEmail: (row['author_email'] as string) ?? null,
    createdAt: (row['created_at'] as string) ?? '',
  };
}

function mapMessage(row: Record<string, unknown>): ChatMessage {
  const rawAtts = Array.isArray(row['attachments'])
    ? (row['attachments'] as Array<Record<string, unknown>>)
    : [];
  return {
    id: row['id'] as string,
    conversationId: (row['conversation_id'] as string) ?? '',
    direction: (row['direction'] as MessageDirection) ?? 'in',
    type: (row['type'] as MessageType) ?? 'text',
    text: (row['text'] as string) ?? null,
    attachments: rawAtts.map(
      (a): MessageAttachment => ({
        type: String(a['type'] ?? 'unsupported'),
        url: (a['url'] as string) ?? null,
        mediaId: (a['mediaId'] as string) ?? null,
        name: (a['name'] as string) ?? null,
      }),
    ),
    status: (row['status'] as MessageStatus) ?? 'received',
    errorMessage: (row['error_message'] as string) ?? null,
    sentBy: (row['sent_by'] as string) ?? null,
    platformTimestamp: (row['platform_timestamp'] as string) ?? null,
    createdAt: (row['created_at'] as string) ?? '',
  };
}

function mapDraft(row: Record<string, unknown>): MessagingOrderDraft {
  const rawItems = Array.isArray(row['items'])
    ? (row['items'] as Array<Record<string, unknown>>)
    : [];
  return {
    id: row['id'] as string,
    // Null (et non '') pour un brouillon né d'une capture : il n'y a
    // aucune conversation derrière, et la distinction pilote l'écran de revue.
    conversationId: (row['conversation_id'] as string) ?? null,
    source: (row['source'] as MessagingOrderDraft['source']) ?? 'conversation',
    target: (row['target'] as MessagingOrderDraft['target']) ?? 'sales',
    imagePaths: Array.isArray(row['image_paths'])
      ? (row['image_paths'] as string[])
      : [],
    proCustomerId: (row['pro_customer_id'] as string) ?? null,
    proCommandeCode: (row['pro_commande_code'] as string) ?? null,
    status: (row['status'] as OrderDraftStatus) ?? 'suggested',
    confidence: row['confidence'] != null ? Number(row['confidence']) : null,
    customer: {
      name: (row['customer_name'] as string) ?? null,
      phone: (row['customer_phone'] as string) ?? null,
      address: (row['customer_address'] as string) ?? null,
    },
    items: rawItems.map((it): OrderDraftItem => {
      const rawCands = Array.isArray(it['candidates'])
        ? (it['candidates'] as Array<Record<string, unknown>>)
        : [];
      return {
        label: String(it['label'] ?? ''),
        quantity: Number(it['quantity'] ?? 1),
        unitPrice: it['unitPrice'] != null ? Number(it['unitPrice']) : null,
        articleId: (it['articleId'] as string) ?? null,
        articleName: (it['articleName'] as string) ?? null,
        candidates: rawCands.map(
          (c): OrderDraftCandidate => ({
            articleId: String(c['articleId'] ?? ''),
            name: String(c['name'] ?? ''),
            reference: (c['reference'] as string) ?? null,
            unitPrice: c['unitPrice'] != null ? Number(c['unitPrice']) : null,
          }),
        ),
      };
    }),
    notes: (row['notes'] as string) ?? null,
    salesDocumentId: (row['sales_document_id'] as string) ?? null,
    createdAt: (row['created_at'] as string) ?? '',
  };
}
